import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  type ContractService,
  errors,
  paxRoute,
  type RouteAuth,
  sessionRequired,
} from "../../common";
import {
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import {
  CommentIdParams,
  CommentVoteCount,
  CreateCommentRequest,
  CreateCommentResponse,
  GetCommentResponse,
  ListCommentsQuery,
  ListCommentsResponse,
  ListRepliesQuery,
  ListRepliesResponse,
  SetCommentVoteRequest,
  UpdateCommentRequest,
  UpdateCommentResponse,
} from "./schemas";

export const service = {
  group: "comments",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Comments API",
  description: "Threaded comments and votes on published presets, with tombstones and owner pins.",
} as const satisfies ContractService;

/**
 * Reads are public. A session, when present, adds viewer state.
 */
const readerAuth: RouteAuth = {
  kind: "public",
  note: "A Better Auth session, when present, adds viewer state.",
};

export const noSuchComment = {
  NOT_FOUND: { message: "No comment has that id." },
} as const;

export const notPublished = {
  CONFLICT: {
    message: "The comment is deleted or removed, so its votes can no longer change.",
  },
} as const;

const noSuchPreset = "No preset has that slug.";
const cannotVote = "Connect an account before voting.";
const cannotPin = "Only the preset's owner can pin comments on it.";
const cannotAuthor = "Connect an account before commenting.";

export const list = oc
  .meta(
    paxRoute({ auth: readerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/comments/v1alpha1",
      operationId: "comments.list",
      summary: "List a preset's comments.",
      description:
        "Returns one page of a published preset's top-level comments, each with a reply count. Replies are loaded on demand. Tombstones keep their place and author.",
      inputStructure: "detailed",
      successDescription: "A page of the preset's comments.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "The query is malformed, or the cursor belongs to another preset or sort.",
    },
    NOT_FOUND: { message: noSuchPreset },
  })
  .input(z.object({ query: ListCommentsQuery }))
  .output(ListCommentsResponse);

export const create = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/comments/v1alpha1",
      operationId: "comments.create",
      summary: "Comment on a preset.",
      description:
        "Posts on a published preset, or replies to one of its published top-level comments. Resolves up to ten distinct @[profileId] references to existing profiles. Automod checks the body first, with the parent as context for a reply; a refused comment isn't posted.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new comment.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    BAD_REQUEST: {
      message:
        "The body is malformed or outside 1–5,000 code points, or parentId is not a top-level comment on this preset.",
    },
    ...sessionRequired,
    FORBIDDEN: { message: cannotAuthor },
    NOT_FOUND: { message: "The preset, or the parent comment, does not exist." },
    CONFLICT: { message: "The parent comment is deleted or removed and accepts no new replies." },
  })
  .input(z.object({ body: CreateCommentRequest }))
  .output(CreateCommentResponse);

export const get = oc
  .meta(
    paxRoute({ auth: readerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/comments/v1alpha1/{commentId}",
      operationId: "comments.get",
      summary: "Get one comment.",
      description:
        "Returns a comment on a published preset, its top-level parent and the replies around it when it is a reply, and the subject so a deep link renders immediately.",
      inputStructure: "detailed",
      successDescription: "The comment.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...noSuchComment,
  })
  .input(z.object({ params: CommentIdParams }))
  .output(GetCommentResponse);

export const update = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/comments/v1alpha1/{commentId}",
      operationId: "comments.update",
      summary: "Edit a comment.",
      description:
        "Replaces the body of a published comment the user wrote. The previous body is kept as a revision and editedAt is set; resending the current body changes nothing. Mentions are resolved again from the new body. Automod checks a changed body first; a refused edit isn't saved.",
      inputStructure: "detailed",
      successDescription: "The edited comment.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: {
      message: "A guest made the request, or the user is not the author.",
    },
    ...noSuchComment,
    CONFLICT: {
      message: "The comment is deleted or removed. Only published comments can be edited.",
    },
  })
  .input(z.object({ params: CommentIdParams, body: UpdateCommentRequest }))
  .output(UpdateCommentResponse);

export const remove = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/comments/v1alpha1/{commentId}",
      operationId: "comments.delete",
      summary: "Delete your comment.",
      description:
        "The author's comment becomes a tombstone that keeps its place and author. Its body remains private in storage, and existing replies stay readable. Deleting it again succeeds.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The comment is deleted.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: {
      message: "A guest made the request, or the user is not the author.",
    },
    ...noSuchComment,
  })
  .input(z.object({ params: CommentIdParams }));

export const listReplies = oc
  .meta(
    paxRoute({ auth: readerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/comments/v1alpha1/{commentId}/replies",
      operationId: "comments.replies.list",
      summary: "List a comment's replies.",
      description:
        "Pages through replies to one top-level comment. Omit cursor to open a collapsed thread from its oldest reply, then pass nextCursor to load more. A reply's siblings cursors page older or newer from that reply. A reply id is not a valid parent for this endpoint.",
      inputStructure: "detailed",
      successDescription: "A page of replies.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "The query is malformed, or commentId names a reply rather than a top-level comment.",
    },
    ...noSuchComment,
  })
  .input(z.object({ params: CommentIdParams, query: ListRepliesQuery }))
  .output(ListRepliesResponse);

export const setVote = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/comments/v1alpha1/{commentId}/vote",
      operationId: "comments.vote.set",
      summary: "Vote on a comment.",
      description:
        "Records the user's single vote on a published comment. Repeating the same value changes nothing; sending the other value replaces the vote.",
      inputStructure: "detailed",
      successDescription: "The like count after the request.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: cannotVote },
    ...noSuchComment,
    ...notPublished,
  })
  .input(z.object({ params: CommentIdParams, body: SetCommentVoteRequest }))
  .output(CommentVoteCount);

export const clearVote = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/comments/v1alpha1/{commentId}/vote",
      operationId: "comments.vote.delete",
      summary: "Withdraw a vote.",
      description:
        "Removes the user's vote on a published comment. Succeeds even when no vote exists.",
      inputStructure: "detailed",
      successDescription: "The like count after the request.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: cannotVote },
    ...noSuchComment,
    ...notPublished,
  })
  .input(z.object({ params: CommentIdParams }))
  .output(CommentVoteCount);

export const pin = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/comments/v1alpha1/{commentId}/pin",
      operationId: "comments.pin.set",
      summary: "Pin a comment.",
      description:
        "Pins a published top-level comment, putting it at the end of the preset's pinned comments, which lead every sort. Only the preset's owner may pin, and a pinned comment leaves the sorted pages so it is never listed twice. Pinning an already pinned comment changes nothing.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The comment is pinned.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: cannotPin },
    ...noSuchComment,
    CONFLICT: {
      message:
        "The preset already has three pinned comments, or the comment is a reply or a tombstone.",
    },
  })
  .input(z.object({ params: CommentIdParams }));

export const unpin = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/comments/v1alpha1/{commentId}/pin",
      operationId: "comments.pin.delete",
      summary: "Unpin a comment.",
      description:
        "Returns a pinned comment to the sorted pages, leaving the order of the remaining pins unchanged. Succeeds even when the comment is not pinned.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The comment is not pinned.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: cannotPin },
    ...noSuchComment,
  })
  .input(z.object({ params: CommentIdParams }));
